import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container, Header, SpaceBetween, Button, Alert, FormField, Select, ProgressBar, Box
} from '@cloudscape-design/components';
import { productImageGenApi } from './productImageGenApi.js';

const CATEGORIES = [
  'Apparel & Fashion', 'Footwear', 'Beauty & Skincare', 'Electronics & Gadgets',
  'Home & Kitchen', 'Food & Beverages', 'Health & Wellness', 'Baby & Kids',
  'Pet Products', 'Jewelry & Accessories', 'Fitness & Sports Equipment',
  'Bags & Luggage', 'Home Decor & Furniture', 'Personal Care & Grooming',
  'Stationery & Office Supplies', 'Eyewear',
].map(c => ({ label: c, value: c }));

const MAX_PREVIEWS = 6;

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function GenerateSection({ onGenComplete }) {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [username, setUsername] = useState('unknown');
  const fileInputRef = useRef(null);

  useEffect(() => {
    productImageGenApi.listBrands().then(data => {
      const opts = (data.brands || []).map(b => ({ label: b.brand_name, value: b.brand_id }));
      setBrands(opts);
      if (opts.length > 0) setSelectedBrand(opts[0]);
    }).catch(() => {});

    const waitUser = async () => {
      let retries = 5;
      while (retries-- > 0) {
        const user = window?.harmony?.api?.getUserName?.();
        if (user) { setUsername(user); return; }
        await new Promise(r => setTimeout(r, 300));
      }
    };
    waitUser();
  }, []);

  const previews = useMemo(() => {
    return selectedFiles.slice(0, MAX_PREVIEWS).map(f => ({ file: f, url: URL.createObjectURL(f) }));
  }, [selectedFiles]);

  useEffect(() => { return () => previews.forEach(p => URL.revokeObjectURL(p.url)); }, [previews]);

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) { setSelectedFiles(files); setError(''); setSuccess(''); }
  };

  const handleGenerate = async () => {
    if (!selectedBrand) { setError('Select a brand first'); return; }
    if (selectedFiles.length === 0) { setError('Select product images'); return; }

    setGenerating(true);
    setError('');
    setSuccess('');
    setProgress({ completed: 0, total: selectedFiles.length });

    try {
      const runId = productImageGenApi.createRunId(username);
      await productImageGenApi.generateImages(
        selectedBrand.value, selectedFiles, category.value, runId, username,
        (p) => setProgress(p)
      );
      setSuccess(`Generated images for ${selectedFiles.length} product${selectedFiles.length > 1 ? 's' : ''} 🎉`);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onGenComplete) onGenComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Container header={<Header variant="h2" description="Select brand, upload product images, choose category">Generate PDP Images</Header>}>
      <SpaceBetween size="m">
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}
        {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

        <SpaceBetween direction="horizontal" size="m">
          <FormField label="Brand">
            <Select selectedOption={selectedBrand} onChange={({ detail }) => setSelectedBrand(detail.selectedOption)}
              options={brands} placeholder="Select brand..." disabled={generating}
              empty="No brands — create one in Brand Setup tab" />
          </FormField>
          <FormField label="Category">
            <Select selectedOption={category} onChange={({ detail }) => setCategory(detail.selectedOption)}
              options={CATEGORIES} disabled={generating} />
          </FormField>
        </SpaceBetween>

        <FormField label="Product Images" description="Upload one image per product. 6 PDP images will be generated for each.">
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFiles} disabled={generating} />
        </FormField>

        {selectedFiles.length > 0 && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>
              {selectedFiles.length} product{selectedFiles.length !== 1 ? 's' : ''} selected
            </Box>
            <div className="file-preview-grid">
              {previews.map((p, i) => (
                <div key={i} className="file-preview-item">
                  <img src={p.url} alt={p.file.name} className="file-preview-thumb" />
                  <div className="file-preview-info">
                    <span className="file-preview-name" title={p.file.name}>
                      {p.file.name.length > 18 ? p.file.name.slice(0, 16) + '...' : p.file.name}
                    </span>
                    <span className="file-preview-size">{formatSize(p.file.size)}</span>
                  </div>
                </div>
              ))}
              {selectedFiles.length > MAX_PREVIEWS && (
                <div className="file-preview-more">+{selectedFiles.length - MAX_PREVIEWS} more</div>
              )}
            </div>
          </div>
        )}

        {generating && (
          <ProgressBar
            value={progress.phase === 'done' ? 100 : progress.phase === 'generating' ? 50 : 10}
            additionalInfo={
              progress.phase === 'uploading' ? 'Uploading images...' :
              progress.phase === 'generating' ? 'Generating 6 PDP images per product... (this takes ~1 minute)' :
              progress.phase === 'done' ? 'Done ✅' : 'Processing...'
            }
            label="Generation progress"
            status={progress.phase === 'done' ? 'success' : 'in-progress'}
          />
        )}

        {selectedFiles.length > 0 && (
          <Button variant="primary" onClick={handleGenerate} loading={generating} disabled={generating || !selectedBrand}>
            🚀 Generate {selectedFiles.length * 6} Images ({selectedFiles.length} product{selectedFiles.length > 1 ? 's' : ''} × 6)
          </Button>
        )}
      </SpaceBetween>
    </Container>
  );
}
